<script setup lang="ts">
import { ArrowRight, BookOpen, Boxes, Clock3, Cloud, DatabaseBackup, Route, Search, ShieldCheck, Wrench } from "@lucide/vue";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import LoadingState from "../components/LoadingState.vue";
import { useContent } from "../composables/useContent";
import { getDisplaySummary, getDisplayTitle, getPrimaryLabel, getRecordSection } from "../lib/content";

const router = useRouter();
const query = ref("");
const { records, loading, error } = useContent();
const published = computed(() => records.value.filter((record) => record.editorial_status === "published"));
const guides = computed(() => published.value.filter((record) => getRecordSection(record) === "knowledge"));
const leadGuide = computed(() => guides.value.find((record) => record.id === "vpsknow-b3fdcc93e8b1a37df87f35b6") ?? guides.value[0]);
const readingList = computed(() => guides.value.filter((record) => record.id !== leadGuide.value?.id).slice(0, 4));
const verifiedDirectory = computed(() => published.value.filter((record) => record.content_type === "directory_entry").slice(0, 6));

const paths = [
  {
    index: "01",
    label: "新服务器上线",
    note: "先保住登录通道，再做补丁、封禁与基础加固",
    to: "/entry/vpsknow-b3fdcc93e8b1a37df87f35b6",
    icon: ShieldCheck,
    primary: true,
  },
  {
    index: "02",
    label: "网站部署",
    note: "从 Dockerfile 到 Compose 的可回滚发布",
    to: "/entry/vpsknow-1f2c1edfe2f6543b19b3c905",
    icon: Boxes,
  },
  {
    index: "03",
    label: "网络排障",
    note: "沿请求路径定位 Nginx 403、404 与 502",
    to: "/entry/vpsknow-3e438f184ea3c44db640dbe2",
    icon: Route,
  },
  {
    index: "04",
    label: "备份恢复",
    note: "用 Restic 快照和恢复演练保住数据",
    to: "/entry/vpsknow-538ee770433880175190169a",
    icon: DatabaseBackup,
  },
];

function search() {
  void router.push({ name: "library", query: query.value.trim() ? { q: query.value.trim() } : {} });
}
</script>

<template>
  <section class="home-hero">
    <div class="page-width hero-inner">
      <div class="hero-copy">
        <span class="hero-eyebrow">VPScope · 中文服务器实践</span>
        <h1>从真实问题出发，找到能验证的方案</h1>
        <p>部署、排障、安全与选型指南，写清前提、检查方法和回滚路径。</p>
        <form class="hero-search" role="search" @submit.prevent="search">
          <Search :size="20" aria-hidden="true" />
          <input v-model="query" type="search" placeholder="搜索问题、命令或服务" aria-label="搜索全站内容" />
          <button type="submit"><span>搜索</span><ArrowRight :size="17" /></button>
        </form>
        <div class="hero-quick-links">
          <RouterLink to="/tools/cidr"><Wrench :size="16" />规划 CIDR 子网</RouterLink>
          <RouterLink to="/tools/time"><Clock3 :size="16" />转换时间戳</RouterLink>
          <RouterLink to="/library?section=resources"><Cloud :size="16" />比较云服务</RouterLink>
        </div>
      </div>

      <nav class="intent-paths" aria-label="实战路径">
        <div class="intent-heading"><strong>按任务进入</strong><span>{{ paths.length }} 条路径</span></div>
        <RouterLink class="intent-primary" :to="paths[0].to">
          <component :is="paths[0].icon" :size="25" aria-hidden="true" />
          <span><small>{{ paths[0].index }} / 新机第一步</small><strong>{{ paths[0].label }}</strong><em>{{ paths[0].note }}</em></span>
          <ArrowRight :size="19" aria-hidden="true" />
        </RouterLink>
        <ol class="intent-secondary">
          <li v-for="item in paths.slice(1)" :key="item.label">
            <RouterLink :to="item.to">
            <component :is="item.icon" :size="20" aria-hidden="true" />
            <span><span class="intent-label"><small>{{ item.index }}</small><strong>{{ item.label }}</strong></span><small>{{ item.note }}</small></span>
            <ArrowRight :size="17" aria-hidden="true" />
            </RouterLink>
          </li>
        </ol>
      </nav>
    </div>
  </section>

  <section class="home-section guide-section">
    <div class="page-width">
      <div class="section-heading home-heading">
        <div><span>编辑精选</span><h2>从一篇可以执行的指南开始</h2><p>每篇都包含验证步骤、失败路径与时间边界。</p></div>
        <RouterLink to="/library?section=knowledge">全部指南 <ArrowRight :size="16" /></RouterLink>
      </div>
      <LoadingState v-if="loading || error" :error="error" />
      <div v-else class="guide-showcase">
        <RouterLink v-if="leadGuide" class="lead-guide" :to="`/entry/${leadGuide.id}`">
          <div class="lead-guide-icon"><ShieldCheck :size="26" /></div>
          <span>{{ getPrimaryLabel(leadGuide) }}</span>
          <h3>{{ getDisplayTitle(leadGuide) }}</h3>
          <p>{{ getDisplaySummary(leadGuide) }}</p>
          <strong>阅读指南 <ArrowRight :size="17" /></strong>
        </RouterLink>
        <div class="guide-reading-list">
          <RouterLink v-for="(record, index) in readingList" :key="record.id" :to="`/entry/${record.id}`">
            <small>{{ String(index + 2).padStart(2, "0") }}</small>
            <span><strong>{{ getDisplayTitle(record) }}</strong><em>{{ getDisplaySummary(record) }}</em></span>
            <ArrowRight :size="17" aria-hidden="true" />
          </RouterLink>
        </div>
      </div>
    </div>
  </section>

  <section class="resource-band">
    <div class="page-width resource-layout">
      <div class="resource-intro"><BookOpen :size="22" /><h2>经过核对的官方入口</h2><p>直接到产品与项目官网，不带登录包装、推荐参数或过期优惠。</p><RouterLink to="/tools">浏览全部资源 <ArrowRight :size="16" /></RouterLink></div>
      <div class="resource-links">
        <RouterLink v-for="record in verifiedDirectory" :key="record.id" :to="`/entry/${record.id}`">
          <span><strong>{{ record.title.split('：')[0] }}</strong><small>{{ record.summary }}</small></span>
          <ArrowRight :size="16" />
        </RouterLink>
      </div>
    </div>
  </section>
</template>
